import { Box, InputAdornment } from '@mui/material';
import { FilledInputProps } from '@mui/material/FilledInput';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import { InputLabelProps as MuiInputLabelProps } from '@mui/material/InputLabel';
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import React from 'react';

import ErrorIcon from '../../assets/icons/Error';
import colors from '../../constants/colors';
import { RemoveButton } from '../styledComponents/IconButtons';

export type StyledTextFieldProps = MuiTextFieldProps & {
  showClear?: boolean;
  handleClear?: () => void;
  optional?: boolean;
  /**
   * Legacy escape hatches preserved through the MUI v9 upgrade. The wrapper maps
   * them onto slotProps.{input,htmlInput,inputLabel} so existing call sites keep
   * working without each migrating to slotProps directly.
   */
  InputProps?: Partial<FilledInputProps>;
  inputProps?: InputBaseComponentProps;
  InputLabelProps?: Partial<MuiInputLabelProps>;
};

const StyledTextField = styled(
  ({
    showClear,
    handleClear,
    optional,
    InputProps,
    inputProps,
    InputLabelProps,
    ...props
  }: StyledTextFieldProps) => (
    <MuiTextField
      label={
        <Box sx={{ color: colors.csaaBlack }}>
          {props.label}
          {optional && (
            <Box
              component="span"
              sx={{
                fontStyle: 'italic',
                color: colors.csaaMid,
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: 14,
                pr: 1,
              }}
            >
              {' '}
              Optional
            </Box>
          )}
        </Box>
      }
      {...props}
      slotProps={{
        input: {
          // disableUnderline: true,
          sx: {
            fontSize: { mobile: 16, tablet: 18 },
            fontFamily: 'Cabin',
            color: colors.csaaMid,
          },
          ...(showClear && handleClear
            ? {
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ display: { tablet: 'none', desktop: 'none' } }}
                  >
                    <RemoveButton
                      aria-label="Clear input"
                      onClick={handleClear}
                      onMouseDown={(
                        event: React.MouseEvent<HTMLButtonElement>
                      ) => event.preventDefault()}
                    />
                  </InputAdornment>
                ),
              }
            : {}),
          ...(props.error
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <ErrorIcon color="error" />
                  </InputAdornment>
                ),
              }
            : {}),
          ...InputProps,
        },

        htmlInput: inputProps,

        inputLabel: InputLabelProps,

        formHelperText: {
          sx: {
            fontWeight: 400,
            fontSize: { mobile: 14, tablet: 16 },
            maxWidth: [1, 500, 500],
            fontFamily: 'Cabin',
            fontKerning: 'normal',
          },
        },
      }}
    />
  )
)(({ theme }) => ({
  '& .MuiFilledInput-root': {
    border: '1px solid #ABAFBF',
    overflow: 'hidden',
    borderRadius: 4,
    backgroundColor: '#FFFFFF',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    '&:hover': {
      backgroundColor: '#FFFFFF',
      '&.MuiFilledInput-root:before': {
        borderBottom: 'none',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow: `inset 0px 0px 0px 2px ${colors.csaaBlack}`,
      borderColor: 'transparent',
      '&:after': {
        borderBottom: 'none',
      },
      '&.Mui-error': {
        boxShadow: `inset 0px 0px 0px 2px ${theme.palette.error.main}`,
      },
    },
    '&:after': {
      borderBottom: 'none',
    },
    '&:before': {
      borderBottom: 'none',
    },
    '&.Mui-error': {
      border: `1px solid ${theme.palette.error.main}`,
    },
    '& .MuiInputBase-input': {
      paddingBottom: theme.spacing(0.5),
    },
    'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    'input[type=number]': {
      MozAppearance: 'textfield',
    },
  },
  '& .MuiInputLabel-root ': {
    fontFamily: 'Cabin',
    '&.MuiFormLabel-filled': {
      fontWeight: 700,
    },
    '&.Mui-focused': {
      fontWeight: 700,
    },
  },
}));

export default StyledTextField;
